/**
 * BonsaitForms - Handles form spam filtering and validation
 * 
 * Uses HPI (Human Probability Index) to detect and prevent bot submissions
 * 
 * @example
 * // Initialize spam protection for all forms
 * BonsaitForms.setup();
 * 
 * @example
 * // Initialize spam protection for specific form
 * BonsaitForms.setupForm('contact-form');
 * 
 * @example
 * // Get current HPI score
 * const score = BonsaitForms.getHumanScore();
 */
export class BonsaitForms {
    
    // Private static properties for behavior tracking
    static _b_mm = false;   // Mouse movement detected
    static _b_mc = false;   // Mouse click detected
    static _b_ic = false;   // Input/control interaction detected
    static _b_tb = false;   // Tab key usage detected
    static _b_ico = null;   // Cookie presence
    static _b_tp = false;   // Time on page (1.5s threshold)

    /**
     * Initialize spam protection for all forms on the page
     * Automatically adds HPI tracking to forms with 'vlt' input
     */
    static setup() {
        try {
            const forms = document.forms;
            if (!forms) return;

            // Check for cookie presence
            BonsaitForms._b_ico = BonsaitForms._getCookie('fcode');
            
            // Time on page tracker (1.5 seconds)
            setTimeout(() => {
                BonsaitForms._b_tp = true;
            }, 1500);

            // Track input/control interactions
            const inputs = document.querySelectorAll('label, input.form-control, select');
            if (inputs) {
                for (let i = 0; i < inputs.length; i++) {
                    inputs[i].addEventListener('mousedown', () => {
                        if (!BonsaitForms._b_ic) {
                            BonsaitForms._b_ic = true;
                        }
                    });
                }
            }

            // Track mouse clicks
            window.addEventListener('mousedown', () => {
                if (!BonsaitForms._b_mc) {
                    BonsaitForms._b_mc = true;
                }
            });

            // Track mouse movements
            window.addEventListener('mousemove', () => {
                if (!BonsaitForms._b_mm) {
                    BonsaitForms._b_mm = true;
                }
            });

            // Track tab key usage
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    if (!BonsaitForms._b_tb) {
                        BonsaitForms._b_tb = true;
                    }
                }
            });

            // Add submit handlers to forms with 'vlt' input
            for (let i = 0; i < forms.length; i++) {
                const form = forms[i];
                const vltInput = form.querySelector("input[name='vlt']");
                
                if (vltInput) {
                    form.addEventListener('submit', function(e) {
                        e.preventDefault();
                        
                        const hpiValue = BonsaitForms.getHumanScore();
                        BonsaitForms._injectHPIInput(this, hpiValue);
                        
                        // console.log(`BonsaitForms: Form submitted with HPI score ${hpiValue}`);
                        this.submit();
                    });
                }
            }
        } catch (error) {
            console.error('BonsaitForms.setup error:', error);
        }
    }

    /**
     * Initialize spam protection for a specific form by ID
     * @param {string} formId - The ID of the form to protect
     */
    static setupForm(formId) {
        if (!formId) {
            console.warn('BonsaitForms.setupForm: formId is required');
            return false;
        }

        try {
            const form = document.querySelector('#' + formId);
            if (!form) {
                console.warn(`BonsaitForms.setupForm: Form with id '${formId}' not found`);
                return false;
            }

            form.addEventListener('submit', function(e) {
                e.preventDefault();
                
                const hpiValue = BonsaitForms.getHumanScore();
                BonsaitForms._injectHPIInput(this, hpiValue);
                
                this.submit();
            });
            
            return true;
        } catch (error) {
            console.error('BonsaitForms.setupForm error:', error);
            return false;
        }
    }

    /**
     * Get the current Human Probability Index score
     * Score ranges from 0-100, with higher values indicating more human-like behavior
     * 
     * The HPI is calculated based on:
     * - Mouse movements (-10 if missing)
     * - Mouse clicks (-10 if missing)
     * - Input field interactions (-10 if missing)
     * - Tab key usage (overrides above 3 if present)
     * - Time spent on page (-10 if < 1.5s)
     * - Cookie presence (-10 if missing)
     * 
     * @returns {number} HPI score (0-100)
     */
    static getHumanScore() {
        let score = 100;

        // If tab key was used, skip mouse/click/interaction checks
        if (!BonsaitForms._b_tb) {
            if (!BonsaitForms._b_mm) score -= 10; // No mouse movement
            if (!BonsaitForms._b_mc) score -= 10; // No mouse click
            if (!BonsaitForms._b_ic) score -= 10; // No input interaction
        }

        // Check cookie presence
        if (!BonsaitForms._b_ico) score -= 10;

        // Check time on page
        if (!BonsaitForms._b_tp) score -= 10;

        return score;
    }

    /**
     * Manually calculate and inject HPI into a form
     * Useful for AJAX form submissions
     * 
     * @param {HTMLFormElement} form - The form element
     * @returns {number} The calculated HPI score
     */
    static injectHPI(form) {
        if (!form) {
            console.warn('BonsaitForms.injectHPI: form element is required');
            return 0;
        }

        const hpi = this.getHumanScore();
        this._injectHPIInput(form, hpi);

        return hpi;
    }

    /**
     * Inject or update HPI value in a form
     * @private
     * @param {HTMLFormElement} form - The form element
     * @param {number} hpiValue - The HPI score to inject
     */
    static _injectHPIInput(form, hpiValue) {
        let hpiInput = form.querySelector('#bonsait-hpi');
        
        if (hpiInput) {
            hpiInput.value = hpiValue;
        } else {
            hpiInput = document.createElement('input');
            hpiInput.id = 'bonsait-hpi';
            hpiInput.type = 'hidden';
            hpiInput.name = 'hpi';
            hpiInput.value = hpiValue;
            form.appendChild(hpiInput);
        }
    }

    /**
     * Get a cookie value by name
     * @private
     * @param {string} name - Cookie name
     * @returns {string} Cookie value or empty string
     */
    static _getCookie(name) {
        const nameEQ = name + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookies = decodedCookie.split(';');
        
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(nameEQ) === 0) {
                return cookie.substring(nameEQ.length, cookie.length);
            }
        }
        
        return '';
    }
}
